import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { METRIC_META, METRIC_CATEGORIES, getAlertStatus } from '@sprintpulse/shared';
import type { MetricName } from '@sprintpulse/shared';
import {
  Chart,
  LineElement, PointElement, LinearScale, CategoryScale,
  Tooltip as ChartTooltip, Filler,
} from 'chart.js';
import clsx from 'clsx';
import { InsightsPanel } from '../components/InsightsPanel';
import { ConfluenceReportModal } from '../components/ConfluenceReportModal';

Chart.register(LineElement, PointElement, LinearScale, CategoryScale, ChartTooltip, Filler);

// ── Metric tile ───────────────────────────────────────────────────────────────

function MetricTile({
  label, value, unit, metricName, onClick, selected, suffix,
}: {
  label: string; value: number | null; unit: string; metricName: MetricName;
  onClick?: () => void; selected?: boolean; suffix?: string;
}) {
  const status = getAlertStatus(metricName, value);
  const statusColor: Record<string, string> = {
    green: 'var(--green)', amber: 'var(--amber)', red: 'var(--red)', unknown: 'var(--text-3)',
  };

  return (
    <button
      onClick={onClick}
      className={clsx(
        'text-left p-4 rounded-lg border transition-all w-full',
        onClick && 'cursor-pointer hover:shadow-sm',
        selected && 'ring-2',
      )}
      style={{
        background: selected ? 'var(--accent-dim)' : 'var(--bg-panel)',
        borderColor: selected ? 'var(--accent)' : 'var(--border)',
        outlineColor: selected ? 'var(--accent)' : 'transparent',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-xs leading-tight" style={{ color: 'var(--text-2)' }}>{label}</span>
        {status !== 'unknown' && (
          <div
            className="w-1.5 h-1.5 rounded-full shrink-0 mt-0.5"
            style={{ background: statusColor[status] }}
          />
        )}
      </div>
      <div className="flex items-baseline gap-1">
        <span
          className="text-2xl font-semibold num leading-none"
          style={{ color: value !== null ? 'var(--text-1)' : 'var(--text-3)' }}
        >
          {value !== null ? (Number.isInteger(value) ? value : value.toFixed(1)) : '—'}
        </span>
        {value !== null && (
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>{unit}</span>
        )}
      </div>
      {suffix && (
        <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>{suffix}</div>
      )}
    </button>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <div
        className="flex items-center gap-3 mb-3"
      >
        <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>
          {title}
        </h2>
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{children}</div>
    </div>
  );
}

// ── Damped trend forecast ─────────────────────────────────────────────────────
// Uses linear regression for direction but dampens the slope each step (70% decay)
// so the forecast converges toward the team's baseline instead of extrapolating wildly.

function linearRegression(values: number[]): { slope: number; intercept: number } {
  const n = values.length;
  const xs = values.map((_, i) => i);
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = values.reduce((a, b) => a + b, 0) / n;
  const denom = xs.reduce((acc, x) => acc + (x - meanX) ** 2, 0);
  const slope = denom === 0 ? 0 : xs.reduce((acc, x, i) => acc + (x - meanX) * (values[i] - meanY), 0) / denom;
  const intercept = meanY - slope * meanX;
  return { slope, intercept };
}

function dampedForecast(values: number[], steps: number, damping = 0.7): number[] {
  const { slope, intercept } = linearRegression(values);
  const lastX = values.length - 1;
  const lastFitted = intercept + slope * lastX;
  const result: number[] = [];
  for (let i = 1; i <= steps; i++) {
    // Each step the slope contribution decays: damping^i
    const dampedSlope = slope * damping ** i;
    result.push(Math.max(0, parseFloat((lastFitted + dampedSlope * i).toFixed(1))));
  }
  return result;
}

function buildForecastLabels(sprintNames: string[], count: number): string[] {
  // Try to extract sprint number from last label and increment
  const last = sprintNames[sprintNames.length - 1];
  const match = last.match(/(\d+)$/);
  if (match) {
    const base = parseInt(match[1], 10);
    const prefix = last.replace(/\d+$/, '');
    return Array.from({ length: count }, (_, i) => `${prefix}${base + i + 1} ◈`);
  }
  return Array.from({ length: count }, (_, i) => `F+${i + 1} ◈`);
}

// ── Trend chart (Chart.js) ────────────────────────────────────────────────────

function TrendChart({ teamId, metric }: { teamId: string; metric: MetricName }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const [showForecast, setShowForecast] = useState(true);
  const [activeProjection, setActiveProjection] = useState<number | null>(null);
  const FORECAST_SPRINTS = 3;

  const { data: trend } = useQuery({
    queryKey: queryKeys.metricTrend(teamId, metric, 8),
    queryFn: () => api.getMetricTrend(teamId, metric, 8),
    enabled: !!teamId,
  });

  useEffect(() => {
    if (!canvasRef.current || !trend?.points.length) return;

    const style = getComputedStyle(document.documentElement);
    const isDark = document.documentElement.classList.contains('dark');
    const accentColor = style.getPropertyValue('--accent').trim() || '#C8691A';
    const gridColor = style.getPropertyValue('--chart-grid').trim() || '#E0DED8';
    const textColor = style.getPropertyValue('--text-3').trim() || '#9B9990';

    const actualLabels = trend.points.map((p) => p.sprintName.replace(/sprint\s*/i, 'S').trim());
    const actualValues = trend.points.map((p) => p.value ?? 0);

    // Build forecast — damped trend using closed sprints only
    const closedPoints = trend.points.filter((p) => p.state === 'closed');
    const forecastLabels = buildForecastLabels(actualLabels, FORECAST_SPRINTS);
    const closedValues = closedPoints.length >= 2
      ? closedPoints.map((p) => p.value ?? 0)
      : actualValues.filter((v) => !isNaN(v));
    const forecastValues = dampedForecast(closedValues, FORECAST_SPRINTS);

    // Active sprint projection — use average of last 3 closed sprints as expected value
    const activeSprint = trend.points.find((p) => p.state === 'active');
    let activeProjectionLocal: number | null = null;
    let activeProjectionIndex: number = -1;

    if (activeSprint) {
      const last3 = closedPoints.slice(-3).map((p) => p.value ?? 0).filter((v) => v > 0);
      if (last3.length > 0) {
        const avg = last3.reduce((a, b) => a + b, 0) / last3.length;
        activeProjectionLocal = parseFloat(avg.toFixed(1));
        activeProjectionIndex = trend.points.findIndex((p) => p.state === 'active');
      }
    }

    setActiveProjection(activeProjectionLocal);

    // Build projected dataset — same positions as actual, but replace active point with projection
    const projectedDataset = actualValues.map((_v, i) => {
      if (i === activeProjectionIndex && activeProjectionLocal !== null) return activeProjectionLocal;
      return null;
    });

    const allLabels = showForecast ? [...actualLabels, ...forecastLabels] : actualLabels;

    // Actual data — null-pad to full label length
    const actualDataset = showForecast
      ? [...actualValues, ...Array(FORECAST_SPRINTS).fill(null)]
      : actualValues;

    // Projected dataset — pad for forecast labels
    const projectedDatasetPadded = showForecast
      ? [...projectedDataset, ...Array(FORECAST_SPRINTS).fill(null)]
      : projectedDataset;

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels: allLabels,
        datasets: [
          {
            label: 'Actual',
            data: actualDataset,
            borderColor: accentColor,
            backgroundColor: isDark ? 'rgba(224,123,48,0.08)' : 'rgba(200,105,26,0.06)',
            borderWidth: 1.5,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: accentColor,
            pointBorderColor: 'transparent',
            fill: true,
            tension: 0.3,
            spanGaps: false,
          },
          // Active sprint projected close — shown always when available
          ...(activeProjectionLocal !== null ? [{
            label: 'Projected close',
            data: projectedDatasetPadded,
            borderColor: 'transparent',
            backgroundColor: 'transparent',
            borderWidth: 0,
            pointRadius: (ctx: any) => projectedDatasetPadded[ctx.dataIndex] !== null ? 5 : 0,
            pointHoverRadius: 7,
            pointBackgroundColor: isDark ? '#facc15' : '#b45309',
            pointBorderColor: isDark ? '#1C1C1A' : '#ffffff',
            pointBorderWidth: 2,
            pointStyle: 'triangle',
            fill: false,
            spanGaps: false,
          }] : []),
          ...(showForecast ? [{
            label: 'Forecast',
            data: [...Array(actualValues.length - 1).fill(null), actualValues[actualValues.length - 1], ...forecastValues],
            borderColor: isDark ? 'rgba(224,123,48,0.5)' : 'rgba(200,105,26,0.45)',
            backgroundColor: 'transparent',
            borderWidth: 1.5,
            borderDash: [5, 4],
            pointRadius: (ctx: any) => ctx.dataIndex >= actualValues.length ? 3 : 0,
            pointHoverRadius: 5,
            pointBackgroundColor: isDark ? 'rgba(224,123,48,0.6)' : 'rgba(200,105,26,0.6)',
            pointBorderColor: 'transparent',
            fill: false,
            tension: 0.3,
            spanGaps: true,
          }] : []),
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 300 },
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: isDark ? '#1C1C1A' : '#FFFFFF',
            borderColor: isDark ? '#2E2E2B' : '#E0DED8',
            borderWidth: 1,
            titleColor: isDark ? '#EFEFED' : '#1A1916',
            bodyColor: isDark ? '#A8A69F' : '#5C5A54',
            padding: 10,
            cornerRadius: 6,
            filter: (item) => item.parsed.y !== null,
            callbacks: {
              title: (items) => items[0].label.replace(' ◈', ' (forecast)'),
              label: (ctx) => {
                const val = ctx.parsed.y !== null ? ctx.parsed.y.toFixed(1) : '—';
                const unit = METRIC_META[metric]?.unit ?? '';
                if (ctx.dataset.label === 'Projected close') return `Projected close: ${val} ${unit}`;
                if (ctx.dataset.label === 'Forecast') return `Forecast: ${val} ${unit}`;
                return `${val} ${unit}`;
              },
            },
          },
        },
        scales: {
          x: {
            border: { display: false },
            grid: { color: gridColor, lineWidth: 1 },
            ticks: {
              color: (ctx) => {
                const label = allLabels[ctx.index] ?? '';
                return label.includes('◈') ? (isDark ? 'rgba(224,123,48,0.7)' : 'rgba(200,105,26,0.7)') : textColor;
              },
              font: { size: 11, family: '"SF Mono", monospace' },
            },
          },
          y: {
            border: { display: false },
            grid: { color: gridColor, lineWidth: 1 },
            ticks: {
              color: textColor,
              font: { size: 11, family: '"SF Mono", monospace' },
              maxTicksLimit: 5,
            },
          },
        },
      },
    });

    return () => { chartRef.current?.destroy(); };
  }, [trend, metric, showForecast]);

  if (!trend?.points.length) {
    return (
      <div className="flex items-center justify-center text-xs" style={{ height: '180px', color: 'var(--text-3)' }}>
        No trend data yet
      </div>
    );
  }

  return (
    <div>
      <div style={{ position: 'relative', height: '180px', width: '100%' }}>
        <canvas ref={canvasRef} />
      </div>
      {/* Forecast toggle */}
      <div className="flex items-center gap-2 mt-3">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <div
            onClick={() => setShowForecast((v) => !v)}
            className="relative w-8 h-4 rounded-full transition-colors"
            style={{ background: showForecast ? 'var(--accent)' : 'var(--border)', flexShrink: 0 }}
          >
            <div
              className="absolute top-0.5 w-3 h-3 rounded-full transition-transform"
              style={{
                background: '#fff',
                left: showForecast ? '18px' : '2px',
                transition: 'left 0.15s',
              }}
            />
          </div>
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>
            Show 3-sprint forecast
          </span>
        </label>
        {activeProjection !== null && (
          <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-3)' }}>
            <span style={{ color: 'var(--amber)', fontSize: '10px' }}>▲</span>
            Projected close: <strong style={{ color: 'var(--text-2)' }}>{activeProjection} {METRIC_META[metric]?.unit}</strong>
          </span>
        )}
        {showForecast && (
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>
            · linear trend · ◈ = projected
          </span>
        )}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function TeamPage(): JSX.Element {
  const { teamId, sprintId } = useParams<{ teamId: string; sprintId?: string }>();
  const navigate = useNavigate();
  const [trendMetric, setTrendMetric] = useState<MetricName>('velocity');
  const [refreshing, setRefreshing] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: sprints = [] } = useQuery({
    queryKey: queryKeys.sprints(teamId!),
    queryFn: () => api.getSprints(teamId!),
    enabled: !!teamId,
  });

  const activeSprint = sprintId ?? sprints[0]?.id;

  const { data: summary, isLoading } = useQuery({
    queryKey: queryKeys.sprintSummary(teamId!, activeSprint!),
    queryFn: () => api.getSprintSummary(teamId!, activeSprint!),
    enabled: !!teamId && !!activeSprint,
  });

  async function handleRefresh() {
    if (refreshing || !teamId) return;
    setRefreshing(true);
    try {
      // Sync sprints from Jira + collect metrics for this team
      await api.syncAndCollect(teamId);
      // 3. Refresh UI — invalidate all team-related queries
      await queryClient.invalidateQueries({ queryKey: queryKeys.sprints(teamId) });
      await queryClient.invalidateQueries({ queryKey: ['trend', teamId] });
      if (activeSprint) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.sprintSummary(teamId, activeSprint) });
      }
    } finally {
      setRefreshing(false);
    }
  }

  if (!teamId) return (
    <div className="p-8 text-sm" style={{ color: 'var(--text-3)' }}>No team selected.</div>
  );

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--text-1)' }}>
            {summary?.team.name ?? '—'}
          </h1>
          <p className="text-sm mt-1 flex items-center gap-2 flex-wrap" style={{ color: 'var(--text-3)' }}>
            {summary?.sprint.name ? (
              <>
                <span>{summary.sprint.name}</span>
                <span style={{ color: 'var(--text-3)' }}>·</span>
                <span>
                  {new Date(summary.sprint.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  {' – '}
                  {new Date(summary.sprint.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
                {summary.sprint.state === 'active' && (
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: 'var(--green-dim)', color: 'var(--green)', fontSize: '10px', letterSpacing: '0.04em' }}
                  >
                    IN PROGRESS
                  </span>
                )}
                {summary.sprint.state === 'closed' && (
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: '10px', letterSpacing: '0.04em' }}
                  >
                    COMPLETED
                  </span>
                )}
                {summary.sprint.state === 'future' && (
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: 'var(--bg-subtle)', color: 'var(--text-3)', fontSize: '10px', letterSpacing: '0.04em' }}
                  >
                    UPCOMING
                  </span>
                )}
              </>
            ) : 'Loading sprint…'}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Refresh metrics */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-all"
            style={{
              background: refreshing ? 'var(--border)' : '#0F5132',
              color: '#ffffff',
              border: '1px solid transparent',
              opacity: refreshing ? 0.7 : 1,
              boxShadow: refreshing ? 'none' : '0 1px 2px rgba(0,0,0,0.15)',
              cursor: refreshing ? 'not-allowed' : 'pointer',
            }}
            title="Collect latest sprint metrics from Jira"
          >
            <span
              style={{
                display: 'inline-block',
                fontSize: '13px',
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
              }}
            >
              ↻
            </span>
            {refreshing ? 'Collecting…' : 'Refresh Metrics'}
          </button>

          {/* Generate sprint report */}
          <button
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-all"
            style={{
              background: '#1D4ED8',
              color: '#ffffff',
              border: '1px solid transparent',
              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
              cursor: 'pointer',
            }}
            title="Generate sprint report in Confluence"
            onClick={() => setShowReportModal(true)}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            Sprint Report
          </button>

          {/* Sprint picker */}
          {sprints.length > 1 && (
            <div className="relative">
              <select
                value={activeSprint ?? ''}
                onChange={(e) => navigate(`/team/${teamId}/sprint/${e.target.value}`)}
                className="text-xs pl-3 pr-8 py-1.5 rounded-md border appearance-none font-medium"
                style={{
                  background: 'var(--bg-panel)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-1)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                }}
              >
                {sprints.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {/* Chevron icon */}
              <div
                className="absolute inset-y-0 right-2 flex items-center pointer-events-none"
                style={{ color: 'var(--text-3)' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-5 h-5 rounded-full border-2 animate-spin"
            style={{ borderColor: 'var(--border-mid)', borderTopColor: 'var(--accent)' }} />
        </div>
      )}

      {!isLoading && !summary && (
        <div
          className="border-2 border-dashed rounded-lg py-16 text-center"
          style={{ borderColor: 'var(--border)' }}
        >
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>
            No sprint data collected yet for this team.
          </p>
        </div>
      )}

      {summary && (
        <>
          {/* Sprint Health */}
          <Section title="Sprint health">
            <MetricTile label="Velocity" value={summary.sprintHealth.velocity} unit="pts" metricName="velocity" onClick={() => setTrendMetric('velocity')} selected={trendMetric === 'velocity'} suffix={summary.sprintHealth.committedPoints > 0 ? `${summary.sprintHealth.committedPoints} pts committed` : undefined} />
            <MetricTile label="Commitment accuracy" value={summary.sprintHealth.commitmentAccuracy} unit="%" metricName="commitment_accuracy" onClick={() => setTrendMetric('commitment_accuracy')} selected={trendMetric === 'commitment_accuracy'} />
            <MetricTile label="Carryover" value={summary.sprintHealth.carryoverRate} unit="%" metricName="carryover_rate" onClick={() => setTrendMetric('carryover_rate')} selected={trendMetric === 'carryover_rate'} />
            <MetricTile label="Scope creep" value={summary.sprintHealth.scopeCreepRate} unit="%" metricName="scope_creep_rate" onClick={() => setTrendMetric('scope_creep_rate')} selected={trendMetric === 'scope_creep_rate'} />
          </Section>

          {/* Quality */}
          <Section title="Quality">
            <MetricTile label="Bugs fixed" value={summary.quality.bugsFixed} unit="bugs" metricName="bugs_fixed" onClick={() => setTrendMetric('bugs_fixed')} selected={trendMetric === 'bugs_fixed'} />
            <MetricTile label="Bugs injected" value={summary.quality.bugInjectionRate} unit="bugs" metricName="bug_injection_rate" onClick={() => setTrendMetric('bug_injection_rate')} selected={trendMetric === 'bug_injection_rate'} />
            <MetricTile label="Defect escape rate" value={summary.quality.defectEscapeRate} unit="%" metricName="defect_escape_rate" onClick={() => setTrendMetric('defect_escape_rate')} selected={trendMetric === 'defect_escape_rate'} />
            <MetricTile label="Reopened" value={summary.quality.reopenedTicketRate} unit="%" metricName="reopened_ticket_rate" onClick={() => setTrendMetric('reopened_ticket_rate')} selected={trendMetric === 'reopened_ticket_rate'} />
          </Section>

          {/* Flow */}
          <Section title="Flow">
            <MetricTile label="Cycle time P50" value={summary.flow.cycleTimeP50Days} unit="days" metricName="cycle_time_p50" onClick={() => setTrendMetric('cycle_time_p50')} selected={trendMetric === 'cycle_time_p50'} />
            <MetricTile label="Cycle time P85" value={summary.flow.cycleTimeP85Days} unit="days" metricName="cycle_time_p85" onClick={() => setTrendMetric('cycle_time_p85')} selected={trendMetric === 'cycle_time_p85'} />
            <MetricTile label="In review P50" value={summary.flow.timeInReviewP50Days} unit="days" metricName="time_in_review_p50" onClick={() => setTrendMetric('time_in_review_p50')} selected={trendMetric === 'time_in_review_p50'} />
            <MetricTile label="In review P85" value={summary.flow.timeInReviewP85Days} unit="days" metricName="time_in_review_p85" onClick={() => setTrendMetric('time_in_review_p85')} selected={trendMetric === 'time_in_review_p85'} />
          </Section>

          {/* Capacity */}
          <Section title="Capacity">
            <MetricTile label="Throughput" value={summary.teamCapacity.throughput} unit="tickets" metricName="throughput" onClick={() => setTrendMetric('throughput')} selected={trendMetric === 'throughput'} />
            <MetricTile label="Unplanned work" value={summary.teamCapacity.unplannedWorkPct} unit="%" metricName="unplanned_work_pct" onClick={() => setTrendMetric('unplanned_work_pct')} selected={trendMetric === 'unplanned_work_pct'} />
          </Section>

          {/* Trend chart */}
          <div
            className="rounded-lg border p-5"
            style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>
                  Trend
                </span>
                <span className="text-sm ml-2 font-medium" style={{ color: 'var(--text-1)' }}>
                  {METRIC_META[trendMetric]?.label}
                </span>
              </div>
              <select
                value={trendMetric}
                onChange={(e) => setTrendMetric(e.target.value as MetricName)}
                className="text-xs px-2 py-1 rounded border appearance-none"
                style={{
                  background: 'var(--bg-subtle)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-2)',
                }}
              >
                {(Object.entries(METRIC_CATEGORIES) as [string, MetricName[]][]).flatMap(([, metrics]) =>
                  metrics.map((m) => (
                    <option key={m} value={m}>{METRIC_META[m].label}</option>
                  ))
                )}
              </select>
            </div>
            <TrendChart teamId={teamId} metric={trendMetric} />
          </div>

          {/* Sprint Insights */}
          <InsightsPanel teamId={teamId!} sprintId={activeSprint!} />
        </>
      )}

      {showReportModal && summary && (
        <ConfluenceReportModal
          teamId={teamId!}
          sprintId={activeSprint!}
          sprintName={summary.sprint.name}
          teamName={summary.team.name}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
}